'use client';

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  examBodyRegions,
  examNarrative,
  examRegionNotes,
  examSystems,
  examTools,
  examVitalSigns,
  type BodyRegion,
  type ExamSystem,
  type RegionNotes,
  type ToolDescriptor,
  type VitalSign,
} from "./_lib/examData";

const defaultCaseIdentifier = "58_42";
const caseIdPattern = /^[0-9]+_[0-9]+$/;

const createId = () => Math.random().toString(36).slice(2, 11);

type PhysicalExamPayload = {
  caseIdentifier: string;
  title: string | null;
  summary: string | null;
  vitalSigns: VitalSign[];
  systems: ExamSystem[];
  regionNotes: RegionNotes;
};

type PhysicalExamApiResponse = {
  data: PhysicalExamPayload | null;
};

function normalizeVitalSigns(input?: VitalSign[]): VitalSign[] {
  const source = input && input.length > 0 ? input : examVitalSigns;
  return source.map((item, index) => ({
    id: item.id ?? `vital-${index}-${createId()}`,
    label: item.label,
    value: item.value ?? "",
    unit: item.unit,
  }));
}

function normalizeSystems(input?: ExamSystem[]): ExamSystem[] {
  const source = input && input.length > 0 ? input : examSystems;
  return source.map((system, systemIndex) => ({
    id: system.id ?? `system-${systemIndex}-${createId()}`,
    name: system.name,
    findings: system.findings.map((finding, findingIndex) => ({
      id: finding.id ?? `finding-${systemIndex}-${findingIndex}-${createId()}`,
      label: finding.label,
      value: finding.value ?? "",
    })),
  }));
}

function normalizeRegionNotes(input?: RegionNotes): RegionNotes {
  const base: RegionNotes = {};
  for (const region of examBodyRegions) {
    const value = input?.[region.id] ?? examRegionNotes[region.id] ?? "";
    base[region.id] = value;
  }
  return base;
}

function BodyMap({
  selectedRegion,
  setSelectedRegion,
}: {
  selectedRegion: string;
  setSelectedRegion: (regionId: string) => void;
}) {
  const shapeClass = (region: string) =>
    selectedRegion === region
      ? "transition fill-cyan-300 stroke-slate-600 hover:fill-cyan-200"
      : "transition fill-slate-300 stroke-slate-600 hover:fill-cyan-200";

  return (
    <svg
      viewBox="0 0 220 560"
      className="h-full w-full max-h-[520px]"
      role="img"
      aria-label="Interactive body map"
    >
      <title>Interactive body map</title>
      <g
        onClick={() => setSelectedRegion("head")}
        className="cursor-pointer"
        aria-label="Head and neck"
      >
        <ellipse
          cx="110"
          cy="70"
          rx="40"
          ry="55"
          className={shapeClass("head")}
        />
      </g>
      <g
        onClick={() => setSelectedRegion("chest")}
        className="cursor-pointer"
        aria-label="Thorax"
      >
        <rect
          x="65"
          y="130"
          width="90"
          height="120"
          rx="40"
          className={shapeClass("chest")}
        />
      </g>
      <g
        onClick={() => setSelectedRegion("abdomen")}
        className="cursor-pointer"
        aria-label="Abdomen"
      >
        <rect
          x="70"
          y="255"
          width="80"
          height="110"
          rx="35"
          className={shapeClass("abdomen")}
        />
      </g>
      <g
        onClick={() => setSelectedRegion("arms")}
        className="cursor-pointer"
        aria-label="Upper limbs"
      >
        <rect
          x="30"
          y="140"
          width="30"
          height="150"
          rx="15"
          className={shapeClass("arms")}
        />
        <rect
          x="160"
          y="140"
          width="30"
          height="150"
          rx="15"
          className={shapeClass("arms")}
        />
      </g>
      <g
        onClick={() => setSelectedRegion("legs")}
        className="cursor-pointer"
        aria-label="Lower limbs"
      >
        <rect
          x="80"
          y="370"
          width="30"
          height="160"
          rx="15"
          className={shapeClass("legs")}
        />
        <rect
          x="110"
          y="370"
          width="30"
          height="160"
          rx="15"
          className={shapeClass("legs")}
        />
      </g>
      <g
        onClick={() => setSelectedRegion("back")}
        className="cursor-pointer"
        aria-label="Back"
      >
        <rect
          x="75"
          y="130"
          width="70"
          height="150"
          rx="32"
          className={shapeClass("back")}
          opacity="0.4"
        />
      </g>
      <g
        onClick={() => setSelectedRegion("neuro")}
        className="cursor-pointer"
        aria-label="Neurological"
      >
        <circle
          cx="110"
          cy="25"
          r="15"
          className={shapeClass("neuro")}
          opacity="0.6"
        />
      </g>
    </svg>
  );
}

function ToolPalette({
  activeTool,
  setActiveTool,
  tools,
}: {
  activeTool: string;
  setActiveTool: (toolId: string) => void;
  tools: ToolDescriptor[];
}) {
  return (
    <div className="grid gap-3">
      {tools.map((tool) => {
        const isActive = tool.id === activeTool;
        return (
          <button
            key={tool.id}
            type="button"
            onClick={() => setActiveTool(tool.id)}
            className={`rounded-lg border px-4 py-3 text-left transition ${
              isActive
                ? "border-cyan-500 bg-cyan-50 text-cyan-900"
                : "border-slate-200 bg-white text-slate-700 hover:border-cyan-300"
            }`}
          >
            <div className="font-semibold">{tool.name}</div>
            <p className="text-sm text-slate-500">{tool.description}</p>
          </button>
        );
      })}
    </div>
  );
}

function VitalSignsEditor({
  vitalSigns,
  onChange,
  onAdd,
}: {
  vitalSigns: VitalSign[];
  onChange: (id: string, key: keyof VitalSign, value: string) => void;
  onAdd: (label: string, unit: string) => void;
}) {
  const [newLabel, setNewLabel] = useState("");
  const [newUnit, setNewUnit] = useState("");

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-800">Vital Signs</h3>
      <div className="mt-4 grid gap-3">
        {vitalSigns.map((vital) => (
          <div key={vital.id} className="grid gap-1">
            <label className="text-sm font-medium text-slate-600">
              {vital.label}
            </label>
            <div className="flex gap-2">
              <input
                value={vital.value}
                onChange={(event) =>
                  onChange(vital.id, "value", event.target.value)
                }
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
              />
              <input
                value={vital.unit ?? ""}
                onChange={(event) =>
                  onChange(vital.id, "unit", event.target.value)
                }
                placeholder="Unit"
                className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>
      <form
        className="mt-4 grid gap-2 border-t border-slate-100 pt-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!newLabel.trim()) {
            return;
          }
          onAdd(newLabel.trim(), newUnit.trim());
          setNewLabel("");
          setNewUnit("");
        }}
      >
        <div className="text-sm font-semibold text-slate-700">
          Add vital sign
        </div>
        <div className="flex gap-2">
          <input
            value={newLabel}
            onChange={(event) => setNewLabel(event.target.value)}
            placeholder="Label"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
          />
          <input
            value={newUnit}
            onChange={(event) => setNewUnit(event.target.value)}
            placeholder="Unit"
            className="w-24 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
          />
          <button
            type="submit"
            className="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-cyan-700"
          >
            Add
          </button>
        </div>
      </form>
    </div>
  );
}

function SystemsEditor({
  systems,
  onUpdate,
  onAddSystem,
  onAddFinding,
}: {
  systems: ExamSystem[];
  onUpdate: (systemId: string, findingId: string, value: string) => void;
  onAddSystem: (name: string) => void;
  onAddFinding: (systemId: string, label: string) => void;
}) {
  const [newSystemName, setNewSystemName] = useState("");
  const [newFindingLabels, setNewFindingLabels] = useState<Record<string, string>>({});

  return (
    <div className="grid gap-6">
      {systems.map((system) => (
        <div
          key={system.id}
          className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-slate-800">
              {system.name}
            </h3>
          </div>
          <div className="mt-4 grid gap-3">
            {system.findings.map((finding) => (
              <div key={finding.id} className="grid gap-1">
                <label className="text-sm font-medium text-slate-600">
                  {finding.label}
                </label>
                <input
                  value={finding.value}
                  onChange={(event) =>
                    onUpdate(system.id, finding.id, event.target.value)
                  }
                  className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
                />
              </div>
            ))}
          </div>
          <form
            className="mt-4 flex gap-2 border-t border-slate-100 pt-4"
            onSubmit={(event) => {
              event.preventDefault();
              const label = (newFindingLabels[system.id] ?? "").trim();
              if (!label) {
                return;
              }
              onAddFinding(system.id, label);
              setNewFindingLabels((current) => ({
                ...current,
                [system.id]: "",
              }));
            }}
          >
            <input
              value={newFindingLabels[system.id] ?? ""}
              onChange={(event) =>
                setNewFindingLabels((current) => ({
                  ...current,
                  [system.id]: event.target.value,
                }))
              }
              placeholder="New finding"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
            />
            <button
              type="submit"
              className="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-cyan-700"
            >
              Add finding
            </button>
          </form>
        </div>
      ))}
      <form
        className="rounded-xl border border-dashed border-cyan-300 bg-cyan-50 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          const name = newSystemName.trim();
          if (!name) {
            return;
          }
          onAddSystem(name);
          setNewSystemName("");
        }}
      >
        <div className="text-sm font-semibold text-cyan-700">
          Add new system
        </div>
        <div className="mt-2 flex gap-2">
          <input
            value={newSystemName}
            onChange={(event) => setNewSystemName(event.target.value)}
            placeholder="System name"
            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
          />
          <button
            type="submit"
            className="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-cyan-700"
          >
            Add system
          </button>
        </div>
      </form>
    </div>
  );
}

function RegionNotesEditor({
  selectedRegion,
  notes,
  regions,
  onUpdate,
}: {
  selectedRegion: string;
  notes: RegionNotes;
  regions: BodyRegion[];
  onUpdate: (regionId: string, text: string) => void;
}) {
  const selectedLabel = useMemo(() => {
    const region = regions.find((entry) => entry.id === selectedRegion);
    return region ? region.label : "Select a region";
  }, [regions, selectedRegion]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-lg font-semibold text-slate-800">
        Region workspace
      </div>
      <div className="mt-1 text-sm text-slate-500">
        {selectedRegion
          ? `Focused on ${selectedLabel}`
          : "Choose a body region to capture examination notes."}
      </div>
      <textarea
        value={notes[selectedRegion] ?? ""}
        onChange={(event) => onUpdate(selectedRegion, event.target.value)}
        placeholder="Record inspection, palpation, and other findings for the selected region."
        disabled={!selectedRegion}
        className="mt-4 h-40 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-100"
      />
    </div>
  );
}

function PhysicalExamWorkspace() {
  const [selectedRegion, setSelectedRegion] = useState<string>("head");
  const [activeTool, setActiveTool] = useState<string>(examTools[0]?.id ?? "");

  const [summary, setSummary] = useState<string>(examNarrative);
  const [vitalSigns, setVitalSigns] = useState<VitalSign[]>(() => normalizeVitalSigns());
  const [systems, setSystems] = useState<ExamSystem[]>(() => normalizeSystems());
  const [regionNotes, setRegionNotes] = useState<RegionNotes>(() => normalizeRegionNotes());

  const [caseIdentifierInput, setCaseIdentifierInput] = useState<string>(defaultCaseIdentifier);
  const [activeCaseIdentifier, setActiveCaseIdentifier] = useState<string>(defaultCaseIdentifier);
  const [lastLoadedSource, setLastLoadedSource] = useState<"database" | "template">("template");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const resetToTemplate = useCallback(() => {
    setVitalSigns(normalizeVitalSigns());
    setSystems(normalizeSystems());
    setSummary(examNarrative);
    setRegionNotes(normalizeRegionNotes());
    setSelectedRegion("head");
    setLastLoadedSource("template");
  }, []);

  const applyServerPayload = useCallback((payload: PhysicalExamPayload) => {
    setVitalSigns(normalizeVitalSigns(payload.vitalSigns));
    setSystems(normalizeSystems(payload.systems));
    setSummary(payload.summary ?? examNarrative);
    setRegionNotes(normalizeRegionNotes(payload.regionNotes));
    setSelectedRegion("head");
    setLastLoadedSource("database");
  }, []);

  const loadCase = useCallback(
    async (identifier: string) => {
      const target = identifier.trim();
      if (!caseIdPattern.test(target)) {
        setErrorMessage("Case identifier must match the format project_case, e.g. 58_42.");
        return;
      }

      setIsLoading(true);
      setStatusMessage(null);
      setErrorMessage(null);

      try {
        const response = await fetch(`/api/physical-exams/${encodeURIComponent(target)}`);
        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }
        const payload = (await response.json()) as PhysicalExamApiResponse;
        if (payload.data) {
          applyServerPayload(payload.data);
          setStatusMessage(`Loaded saved record for case ${target}.`);
        } else {
          resetToTemplate();
          setStatusMessage(`No saved record found for case ${target}. Template loaded.`);
        }
        setActiveCaseIdentifier(target);
        setCaseIdentifierInput(target);
      } catch (error) {
        console.error("Failed to load case", error);
        setErrorMessage("Unable to load the requested physical examination record.");
      } finally {
        setIsLoading(false);
      }
    },
    [applyServerPayload, resetToTemplate],
  );

  useEffect(() => {
    void loadCase(defaultCaseIdentifier);
  }, [loadCase]);

  const updateVitalSign = (id: string, key: keyof VitalSign, value: string) => {
    setVitalSigns((current) =>
      current.map((vital) =>
        vital.id === id
          ? {
              ...vital,
              [key]: value,
            }
          : vital,
      ),
    );
  };

  const addVitalSign = (label: string, unit: string) => {
    setVitalSigns((current) => [
      ...current,
      { id: createId(), label, value: "", unit },
    ]);
  };

  const addSystem = (name: string) => {
    setSystems((current) => [
      ...current,
      {
        id: createId(),
        name,
        findings: [],
      },
    ]);
  };

  const addFinding = (systemId: string, label: string) => {
    setSystems((current) =>
      current.map((system) =>
        system.id === systemId
          ? {
              ...system,
              findings: [
                ...system.findings,
                { id: createId(), label, value: "" },
              ],
            }
          : system,
      ),
    );
  };

  const updateFinding = (systemId: string, findingId: string, value: string) => {
    setSystems((current) =>
      current.map((system) =>
        system.id === systemId
          ? {
              ...system,
              findings: system.findings.map((finding) =>
                finding.id === findingId
                  ? {
                      ...finding,
                      value,
                    }
                  : finding,
              ),
            }
          : system,
      ),
    );
  };

  const updateRegionNote = (regionId: string, text: string) => {
    if (!regionId) {
      return;
    }
    setRegionNotes((current) => ({
      ...current,
      [regionId]: text,
    }));
  };

  const activeToolDescription = useMemo(() => {
    const tool = examTools.find((entry) => entry.id === activeTool);
    return tool ? tool.description : "Select an examination tool.";
  }, [activeTool]);

  const handleLoadClick = useCallback(() => {
    void loadCase(caseIdentifierInput);
  }, [caseIdentifierInput, loadCase]);

  const handleSave = useCallback(async () => {
    const target = caseIdentifierInput.trim();
    if (!caseIdPattern.test(target)) {
      setErrorMessage("Case identifier must match the format project_case, e.g. 58_42.");
      return;
    }

    setIsSaving(true);
    setStatusMessage(null);
    setErrorMessage(null);

    try {
      const response = await fetch(`/api/physical-exams/${encodeURIComponent(target)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          caseIdentifier: target,
          summary,
          title: null,
          vitalSigns,
          systems,
          regionNotes,
        }),
      });

      if (!response.ok) {
        throw new Error(`Save failed with status ${response.status}`);
      }

      const payload = (await response.json()) as PhysicalExamApiResponse;
      if (payload.data) {
        applyServerPayload(payload.data);
      }

      setActiveCaseIdentifier(target);
      setStatusMessage(`Saved examination record for case ${target}.`);
    } catch (error) {
      console.error("Failed to save case", error);
      setErrorMessage("Unable to save the physical examination record. Please retry.");
    } finally {
      setIsSaving(false);
    }
  }, [applyServerPayload, caseIdentifierInput, regionNotes, summary, systems, vitalSigns]);

  return (
    <div className="grid gap-6">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <div className="flex-1">
            <label className="text-sm font-semibold text-slate-700">
              Case identifier (project_case)
            </label>
            <input
              value={caseIdentifierInput}
              onChange={(event) => setCaseIdentifierInput(event.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row">
            <button
              type="button"
              onClick={handleLoadClick}
              disabled={isLoading}
              className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-cyan-300 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isLoading ? "Loading…" : "Load case"}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isLoading}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isSaving ? "Saving…" : "Save case"}
            </button>
          </div>
        </div>
        <div className="mt-3 grid gap-1 text-sm">
          <div className="text-slate-600">
            Active case: <span className="font-semibold text-slate-800">{activeCaseIdentifier}</span>
          </div>
          <div className="text-xs text-slate-500">
            Source: {lastLoadedSource === "database" ? "Database record" : "Template"}
          </div>
          {statusMessage && (
            <div className="rounded-lg bg-cyan-50 px-3 py-2 text-cyan-800">{statusMessage}</div>
          )}
          {errorMessage && (
            <div className="rounded-lg bg-red-50 px-3 py-2 text-red-700">{errorMessage}</div>
          )}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[360px_1fr]">
        <aside className="grid gap-6">
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <h2 className="text-xl font-semibold text-slate-900">Physical Examination Workspace</h2>
            <p className="mt-1 text-sm text-slate-500">
              Select a region, choose an examination tool, and document dynamic findings.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-semibold text-slate-700">Examination tools</div>
            <p className="mt-1 text-xs text-slate-500">{activeToolDescription}</p>
            <div className="mt-4">
              <ToolPalette activeTool={activeTool} setActiveTool={setActiveTool} tools={examTools} />
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm font-semibold text-slate-700">Region quick select</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {examBodyRegions.map((region) => (
                <button
                  key={region.id}
                  type="button"
                  onClick={() => setSelectedRegion(region.id)}
                  className={`rounded-lg border px-3 py-2 text-sm transition ${
                    selectedRegion === region.id
                      ? "border-cyan-500 bg-cyan-50 text-cyan-900"
                      : "border-slate-200 bg-white text-slate-600 hover:border-cyan-300"
                  }`}
                >
                  {region.label}
                </button>
              ))}
            </div>
          </div>
        </aside>
        <main className="grid gap-6">
          <section className="grid gap-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm lg:grid-cols-2">
            <div className="flex flex-col">
              <div className="text-lg font-semibold text-slate-800">Interactive body map</div>
              <div className="mt-2 flex-1">
                <BodyMap selectedRegion={selectedRegion} setSelectedRegion={setSelectedRegion} />
              </div>
            </div>
            <RegionNotesEditor
              selectedRegion={selectedRegion}
              notes={regionNotes}
              regions={examBodyRegions}
              onUpdate={updateRegionNote}
            />
          </section>

          <section className="grid gap-6 lg:grid-cols-2">
            <VitalSignsEditor vitalSigns={vitalSigns} onChange={updateVitalSign} onAdd={addVitalSign} />
            <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-slate-800">Admission summary</h3>
              <textarea
                value={summary}
                onChange={(event) => setSummary(event.target.value)}
                className="mt-3 h-40 w-full resize-none rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-cyan-500 focus:outline-none"
              />
              <p className="mt-2 text-xs text-slate-500">
                Summaries help students correlate vital signs with system-based findings. Encourage descriptive language.
              </p>
            </div>
          </section>

          <section>
            <SystemsEditor systems={systems} onUpdate={updateFinding} onAddSystem={addSystem} onAddFinding={addFinding} />
          </section>
        </main>
      </div>
    </div>
  );
}

export default function Home() {
  return (
    <div className="min-h-screen bg-slate-50 px-6 py-12 lg:px-12">
      <PhysicalExamWorkspace />
    </div>
  );
}
